<template>
  <SoButton
    title="Bulk SMR"
    data-testid="show-bulk-smr"
    prepend-icon="upload"
    large
    @click="showModal = true"
  />
  <SoDialog
    v-model="showModal"
    card-title="Bulk smart meter reads"
    data-testid="bulk-smr-modal"
  >
    <div>
      <p class="mb-4">
        Upload a CSV file containing a list of MPXNs. We will attempt to send an
        XRITM or XIRED flow for each of the smart meters on the meterpoints on
        the list.
      </p>
      <div class="d-flex">
        <SoCheckbox
          v-model="isChecked"
          data-testid="smr-checkbox"
          top-aligned
        />
        <span class="ml-4 text-danger">
          I have ensured the Anomaly Detection Thresholds were adjusted to
          prevent all BOL requests going into quarantine.
        </span>
      </div>
      <p class="text-body-2 mb-4 ml-8 text-danger">*This field is required.</p>
    </div>
    <SoFileInputField
      v-model="file"
      class="mb-4"
      file-types="csv"
      label="Upload CSV"
      placeholder="Select a file"
    />
    <div v-if="!bulkUploadLoading">
      <SoAlert
        v-if="alertState"
        :text="alertState.text"
        :type="alertState.type"
        data-testid="bulk-smr-alert"
      />
      <div class="d-flex justify-end my-3">
        <SoExportCsvButton
          v-if="bulkUploadError"
          :data="downloadConfig.data"
          :labels="downloadConfig.headers"
          :file-name="downloadConfig.fileName"
          button-text="Download Errors"
          prepend-icon="download"
          data-testid="bulk-smart-reads-error-download"
          @export="downloadCsv(bulkUploadError)"
        />
      </div>
    </div>
    <div class="d-flex justify-space-between mt-4">
      <SoButton title="Cancel" large @click="showModal = false" />
      <SoButton
        title="Confirm"
        type="primary"
        large
        :disabled="!enableInteraction"
        :loading="bulkUploadLoading"
        data-testid="submit-smr-button"
        @click="readFile(file[0])"
      />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoFileInputField from "@/components/formControls/SoFileInputField.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import SoAlert, { AlertTypes } from "@/components/SoAlert.vue"
  import { BulkSmartReadsResultExporter } from "@/models/meterpoint/bulkSmartReadResultExporter"
  import SoExportCsvButton from "@/components/dataTable/SoExportCsvButton.vue"

  export default defineComponent({
    components: {
      SoFileInputField,
      SoDialog,
      SoButton,
      SoCheckbox,
      SoAlert,
      SoExportCsvButton,
    },
    data() {
      return {
        showModal: false,
        isChecked: false,
        file: [] as File[],
        current: useCurrentAccountStore(),
        errors: null,
        meterpoints: null as null | Array<string>,
      }
    },
    computed: {
      bulkUploadError() {
        return this.current.bulkUploadErrors.errors
      },
      downloadConfig() {
        const exporter = new BulkSmartReadsResultExporter(this.errors)
        return exporter.getConfig()
      },
      alertState() {
        const { isError, isSuccess, errors } = this.current.bulkUploadErrors

        if (isError)
          return {
            text: "Failed. Please try again.",
            type: "error" as AlertTypes,
          }
        else if (isSuccess && !errors)
          return {
            text: "Succeeded without errors",
            type: "success" as AlertTypes,
          }
        else if (isSuccess && errors)
          return {
            text: "Succeeded with errors. Click the button below to download the errors.",
            type: "warning" as AlertTypes,
          }
        else return null
      },
      bulkUploadLoading() {
        return this.current.bulkUploadErrors.isLoading
      },
      enableInteraction() {
        return this.isChecked && this.file.length > 0
      },
    },
    watch: {
      showModal(val) {
        if (!val) {
          this.current.bulkUploadErrors = {
            isLoading: false,
            isSuccess: false,
            isError: false,
            errors: null,
          }
          this.isChecked = false
          this.file = []
        }
      },
      meterpoints(val) {
        this.current.bulkSmartReads(val)
      },
    },
    methods: {
      downloadCsv(errors) {
        this.errors = errors
      },
      async readFile(file: File) {
        const blob = new Blob([file])
        const blobText = await blob.text()
        this.meterpoints = blobText.split(/\D/g)
      },
    },
  })
</script>
